<script lang="ts">
  import type { ReaderListCategory, ReaderListSource } from './lib/reader-list';
  import { ReaderList } from './lib/reader-list';
  import type { WebRequestConfig } from './types/web-request.interface';
  import ReaderPage from './components/pages/ReaderPage.svelte';
  import ReaderSidebar from './components/pages/ReaderSidebar.svelte';
  import ReaderFrame from './components/pages/ReaderFrame.svelte';
  import ReaderEmptyFavorites from './components/ReaderEmptyFavorites.svelte';
  import { onMount, setContext } from 'svelte';
  import { addToIndex, uniqueBy } from './lib/utils';
  import { favoriteLibraryNames, theme } from './store';
  import PermissionRequest from './components/PermissionRequest.svelte';
  import { getStorage, LsKey, setStorage } from './lib/storage';
  import { Context } from './types/context.constants';
  import { isInstalled } from './lib/helperExtension';
  import Chevron from './components/elements/Chevron.svelte';
  import Tooltip from './components/elements/Tooltip.svelte';
  import Menu from './components/elements/Menu.svelte';
  import { fade } from 'svelte/transition';
  import { ensureHttps, trimEndingSlash } from './lib/url';
  import Newsletter from './components/Newsletter.svelte';
  import InlineSvg from './components/elements/InlineSvg.svelte';
  import Welcome from './components/Welcome.svelte';
  import Sidebar from './components/elements/Sidebar.svelte';

  export let useFavorites = true;
  export let webRequestConfig: WebRequestConfig;

  let permissionChecked = false;
  let permissionGranted = false;
  let sources = [];
  let activeSource = null;
  let featuredSources = [];
  let collapse = false;
  let collapsePosition = '416px';
  let collapseTooltip = 'Collapse side panel';
  let chevronColor = 'var(--color-blue-gray--150)';
  let chevronDirection = 'left';
  let isHelperExtensionInstalled;
  let itemSaved = false;
  let isSubMenu;
  let alternativeLinks = [
    {
      name: 'iStock',
      category: 'Stock Photos',
      url: 'https://bit.ly/33MtRVe',
    },
    {
      name: 'Envato',
      category: 'Stock Photos',
      url: 'https://asteya.network/host/thestocks-api-integration/?target=envato',
    },
    {
      name: 'Shutterstock',
      category: 'Stock Photos',
      url: 'https://bit.ly/31EBYR7',
    },
    {
      name: 'Shutterstock',
      category: 'Stock Videos',
      url: 'https://asteya.network/host/thestocks-api-integration/?target=shutterstock&cat=videos',
    },
    {
      name: 'iStock',
      category: 'Stock Videos',
      url: 'https://asteya.network/host/thestocks-api-integration/?target=istock&cat=videos',
    },
    {
      name: 'Envato',
      category: 'Stock Videos',
      url: 'https://bit.ly/3dk2AQC',
    },
    {
      name: 'Creative Flow',
      category: 'Image Tools',
      url: 'http://asteya.network/host/thestocks-api-integration/?target=shutterstock&cat=creativeflow',
    },
    {
      name: 'Creative Flow',
      category: 'Design Tools',
      url: 'http://asteya.network/host/thestocks-api-integration/?target=shutterstock&cat=creativeflow',
    },
  ];
  let defaultCategory: ReaderListCategory;
  let savedItemsCategory: ReaderListCategory;
  let appName = 'The Stocks 3';
  let appDescription =
    'Best royalty free stock photos, videos, mockups, icons and fonts!';
  // let isProductHuntDismissed = getStorage(LsKey.PRODUCT_HUNT_DISMISSED, false);
  // let isCatVisible = false;
  // let isBaloonVisible = false;
  // let isCloseVisible = false;
  let loadIframe = false;
  let loadReader = false;

  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    theme.set({ 'dark-mode': getStorage(LsKey.DARK_MODE, true) });
  } else {
    theme.set({ 'dark-mode': getStorage(LsKey.DARK_MODE, false) });
  }

  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', (event) => {
      if (event.matches) {
        theme.set({ 'dark-mode': getStorage(LsKey.DARK_MODE, true) });
      } else {
        theme.set({ 'dark-mode': getStorage(LsKey.DARK_MODE, false) });
      }
    });

  $: isDarkMode = $theme['dark-mode'];
  $: onDarkModeChange(isDarkMode);

  function onDarkModeChange(mode: boolean) {
    if (mode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }

  let categories: ReaderListCategory[] = [];
  $: activeCategory = categories[0] || null;
  $: onActiveCategoryChange(activeCategory);
  $: onFavoritesChange($favoriteLibraryNames);
  $: selectedItemUrl = activeSource?.url || featuredSources?.[0].url || sources[0]?.url;
  $: selectedItem = activeSource || featuredSources?.[0] || sources[0];
  $: tempGranted =
    selectedItem?.iframeEnabled ||
    (JSON.stringify(featuredSources).includes(selectedItemUrl) && !selectedItemUrl.includes('https://bit.ly/3sGy0Zd'))    
      ? true
      : permissionGranted;

  setContext(Context.USE_FAVORITE, useFavorites);
  const libraryList = new ReaderList('/data/library.json');

  onMount(async () => {
    tempGranted = true;
    fetchLibraryData();
    checkAsteyaLauncher();

    // if (!isProductHuntDismissed) {
    //   setTimeout(() => {
    //     isCatVisible = true;
    //     setTimeout(() => {
    //       isBaloonVisible = true;
    //       setTimeout(() => {
    //         isCloseVisible = true;
    //       }, 3000);
    //     }, 500);
    //   }, 4500);
    // }

    setTimeout(() => {
      loadReader = true;
    }, 250);

    setTimeout(() => {
      loadIframe = true;
    }, 2000);
  });

  async function fetchLibraryData() {
    const libraryCategories = await libraryList.getList();

    categories = libraryCategories.map(perpareCategory);
  }

  async function checkAsteyaLauncher() {
    isHelperExtensionInstalled = await isInstalled();
  }

  function perpareCategory(category: ReaderListCategory) {
    const icon = `/img/icon/library/${category.id}.svg`;

    return {
      ...category,
      icon,
    };
  }

  function setSources() {
    sources = getSources();
    featuredSources = pickFeaturedSources(sources);
    activeSource = featuredSources?.[0] || sources?.[0];
  }

  let latestFeaturedSource = getStorage(LsKey.LATEST_FEATURED_SOURCE, {
    name: null,
    order: 0,
  });

  function pickFeaturedSources(sources: ReaderListSource[]) {
    if (activeCategory?.name === 'Saved Items') return null;
    let pickedFeaturedSources = sources.filter((source) => source.featured);

    if (!pickedFeaturedSources.length) return null;

    pickedFeaturedSources.forEach((pickedFeaturedSource) => {
      alternativeLinks.forEach((item) => {
        if (item.name === pickedFeaturedSource.name && item.category === activeCategory.name) {
          if (pickedFeaturedSource && !isHelperExtensionInstalled) {
            pickedFeaturedSource.url = item.url;
          }
        }
      });
    });

    const featuredSource =
      pickedFeaturedSources[latestFeaturedSource.order] || pickedFeaturedSources[0];

    setStorage(LsKey.LATEST_FEATURED_SOURCE, {
      name: featuredSource.name,
      order: (latestFeaturedSource.order + 1) % pickedFeaturedSources.length,
    });

    pickedFeaturedSources = [
      ...pickedFeaturedSources.slice(
        latestFeaturedSource.order,
        pickedFeaturedSources.length
      ),
      ...pickedFeaturedSources.slice(0, latestFeaturedSource.order),
    ];

    return pickedFeaturedSources;
  }

  function onActiveCategoryChange(_: ReaderListCategory) {
    const resetLatestFeaturedSource = getStorage(LsKey.LATEST_FEATURED_SOURCE, {
      name: null,
      order: 0,
    });
    setSources();
    setStorage(LsKey.LATEST_FEATURED_SOURCE, resetLatestFeaturedSource);
  }

  function onFavoritesChange(_: string[]) {
    if (!activeCategory || activeCategory.name !== 'Saved Items') {
      return;
    }

    const resetLatestFeaturedSource = getStorage(LsKey.LATEST_FEATURED_SOURCE, {
      name: null,
      order: 0,
    });
    setSources();
    setStorage(LsKey.LATEST_FEATURED_SOURCE, resetLatestFeaturedSource);
  }

  function getCategories(categories: ReaderListCategory[]): ReaderListCategory[] {
    let allSources = [];
    let allFeatured = [];

    categories.forEach((category) =>
      category.sources.forEach((source) => allSources.push(source))
    );

    // allSources = [...new Map(allSources.map((source) => [source.id, source])).values()];
    allSources = uniqueBy(allSources, 'name');
    allFeatured = allSources.filter((source) => source.featured);

    const library: ReaderListCategory = {
      name: 'Home',
      id: 'home',
      icon: '/img/icon/library/library.svg',
      sources: uniqueBy(
        sortSources(allSources, 'Home')
          .slice(0, 50)
          .concat(allFeatured.filter((featured) => featured.hideOnHome !== true)),
        'name'
      ),
    };

    const newsletter: ReaderListCategory = {
      name: 'Newsletter Inbox',
      id: 'newsletter',
      icon: '/img/icon/mail-open.svg',
      sources: [],
    };

    const ddr: ReaderListCategory = {
      name: 'Daily Report',
      id: 'ddr',
      icon: '/img/icon/ddr.svg',
      sources: [],
    };

    defaultCategory = library;
    categories = addToIndex(categories, library, 0);
    categories = addToIndex(categories, newsletter, 1);
    categories = addToIndex(categories, ddr, 2);
    activeCategory = categories[0] || null;
    categories?.forEach((category) => {
      if (window.location.href.includes(category.id)) {
        tempGranted = false;
        activeCategory = categories.find((cat) => cat.id === category.id);
      }
    });

    setSources();

    if (!useFavorites) return categories;

    const favorites: ReaderListCategory = {
      name: 'Saved Items',
      id: 'favorites',
      icon: '/img/icon/library/favorites.svg',
      sources: [],
    };

    savedItemsCategory = favorites;

    return addToIndex(categories, favorites, 3);
  }

  function getSources(): ReaderListSource[] {
    if (!activeCategory) {
      return [];
    }

    if (!useFavorites || activeCategory.name !== 'Saved Items') {
      return activeCategory.sources;
    }

    if (!$favoriteLibraryNames.length) {
      return [];
    }

    const favoriteSources: ReaderListSource[] = [];

    categories.forEach((category) => {
      category.sources.forEach((source) => {
        if ($favoriteLibraryNames.includes(source.name)) {
          favoriteSources.push(source);
        }
      });
    });

    return uniqueBy(favoriteSources, 'name');
  }

  function sortSources(sources: ReaderListSource[], activeCategoryName) {
    if (activeCategoryName !== 'Home') return sources;

    const sortedSources = sources;
    return sortedSources.sort((a, b) => {
      return (
        Number(!!b.iframeEnabled) - Number(!!a.iframeEnabled) ||
        new Date(b.dateAdded || null).getTime() - new Date(a.dateAdded || null).getTime()
      );
    });
  }

  function setCollapse() {
    collapse = !collapse;
    collapse
      ? ((collapsePosition = '128px'),
        (chevronDirection = 'right'),
        (collapseTooltip = 'Expand side panel'))
      : ((collapsePosition = '416px'),
        (chevronDirection = 'left'),
        (collapseTooltip = 'Collapse side panel'));
  }

  function onSaved() {
    itemSaved = true;
    setTimeout(() => {
      itemSaved = false;
    }, 1000);
  }

  let mq = window.matchMedia('(max-width: 600px)');

  $: isMobile = checkMediaQuery(mq);

  function checkMediaQuery(mediaQuery): boolean {
    if (mediaQuery.matches) {
      if (collapse) setCollapse();
      return true;
    } else {
      return false;
    }
  }

  mq.addEventListener('change', () => {
    mq = mq;
  });

  function popStateChange() {
    if (
      trimEndingSlash(window.location.href) === trimEndingSlash(window.location.origin)
    ) {
      activeCategory = defaultCategory;
    } else {
      categories?.forEach((category) => {
        if (window.location.href.includes(category.id)) {
          activeCategory = categories.find((cat) => cat.id === category.id);
        }
      });
    }
  }

  if (mq.matches) {
    window.addEventListener('popstate', function (_) {
      isSubMenu = false;
    });
  }
</script>

<svelte:window on:popstate={popStateChange} />
<svelte:head>
  <title>
    {`${appName} | ${
      activeCategory?.name === 'Home' ? '' : activeCategory?.name + ' |'
    } ${appDescription}`}
  </title>
  {#if process.env.NODE_ENV === 'production' && process.env.APP_ENV !== 'stage'}
  <script src="https://cdn.usefathom.com/script.js" data-site="NROSCJMK" defer></script>
  {/if}
  <link rel="icon" type="image/svg" href="/favicon.svg" />
</svelte:head>

<Welcome />
<Sidebar />
{#if loadReader}
<ReaderPage id="library">
  {#if categories.length && isMobile ? !isSubMenu : true}
  <ReaderSidebar
  className="categories"
  items={getCategories(categories)}
  {collapse}
  {itemSaved}
  {isMobile}
  activeItem={activeCategory}
  on:select={(evt) => {
    activeCategory = evt.detail || null;
    window.history.pushState(
      'https://thestocks.im',
      evt.detail.name,
      evt.detail.id !== 'home' ? evt.detail.id : '/'
      );
      if (evt.detail.id !== 'newsletter') {
        isSubMenu = true;
      }
    }}
      />
      {/if}
      {#if !collapse}
      <div transition:fade>
        <Menu />
      </div>
      <!-- {#if !isMobile && !isProductHuntDismissed}
      <div class="product-hunt">
        <button
        class="close"
        class:visible={isCloseVisible}
        on:click={() => {
          isCatVisible = false;
          isCloseVisible = false;
          isBaloonVisible = false;
              setStorage(LsKey.PRODUCT_HUNT_DISMISSED, true);
            }}
          >
            <InlineSvg src="/img/icon/times.svg" width="14px" height="14px" />
          </button>
          <img
            src="/img/product-cat.png"
            alt="We are on Product Hunt"
            class:visible={isCatVisible}
          />
          <p class:visible={isBaloonVisible}>
            Meow.. We’re live on Product Hunt! Thank you for being here! We ❤️ you! Want
            to thank us? 😻 <br />
            <a
              href={'https://www.producthunt.com/posts/thestocks-v3/'}
              target="_blank"
              on:click={() => {
                isCatVisible = false;
                isCloseVisible = false;
                isBaloonVisible = false;
                setStorage(LsKey.PRODUCT_HUNT_DISMISSED, true);
              }}>We are on Product Hunt →</a
            >
          </p>
        </div>
      {/if} -->
    {/if}
    {#if categories.length && activeCategory.id !== 'newsletter' && activeCategory.id !== 'ddr'}
      <ReaderSidebar
        className="sources"
        items={sources}
        {collapse}
        {isMobile}
        on:saved={onSaved}
        bind:isSubMenu
        listTitle={activeCategory ? activeCategory.name : ''}
        activeItem={activeSource || featuredSources?.[0] || sources[0]}
        featured={featuredSources}
        on:select={(evt) => (activeSource = evt.detail)}
      />
      {#if !isMobile}
        <Tooltip theme="dark" position="right">
          <div
            class="collapse-container"
            on:click={setCollapse}
            style="left: {collapsePosition}"
          >
            <button class="arrow">
              <Chevron
                direction={chevronDirection}
                color={chevronColor}
                width="8"
                height="8"
              />
            </button>
          </div>
          <span slot="tip">{collapseTooltip}</span>
        </Tooltip>
        {#if permissionGranted || tempGranted}
          {#if activeCategory?.name === 'Saved Items' && !sources?.length}
            <ReaderEmptyFavorites />
          {:else if activeSource || featuredSources?.[0] || sources[0]}
            <ReaderFrame
              on:saved={onSaved}
              item={activeSource || featuredSources?.[0] || sources[0]}
            />
          {/if}
        {:else}
          <PermissionRequest
            {webRequestConfig}
            {selectedItemUrl}
            bind:checked={permissionChecked}
            bind:granted={permissionGranted}
          />
        {/if}
      {/if}
    {/if}
    <div class="newsletter" class:visible={activeCategory.id === 'newsletter'}>
      <Newsletter {isMobile} startTimeout={activeCategory.id === 'newsletter'} />
    </div>
    {#if loadIframe}
      <iframe
        class="daily-report"
        class:visible={activeCategory.id === 'ddr'}
        title="Designer Daily Report"
        src={ensureHttps(
          'https://app.designerdailyreport.com/standalone/modal/index.html?host=thestocks.im'
        )}
        frameborder="0"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
        allowfullscreen={true}
        width="100%"
        allow="clipboard-write"
      />
    {/if}
    {#if isMobile}
      <span
        class="badge"
        on:click={() => {
          activeCategory = savedItemsCategory;
          window.history.pushState('https://thestocks.im', 'favorites', 'favorites');
          isSubMenu = true;
        }}
      >
        <InlineSvg src="/img/icon/library/heart-fill.svg" />
        {$favoriteLibraryNames.length}
      </span>
    {/if}
  </ReaderPage>
{/if}

<style lang="scss">
  @import './styles/_mixins.scss';
  .collapse-container {
    position: absolute;
    align-self: center;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 416px;
    transition: all 0.1s ease-in;
    z-index: 1;
    width: 18px;
    height: 42px;
    background-color: var(--color-blue-gray--50);
    border: 1px solid var(--color-blue-gray--100);
    border-left: none;
    border-radius: 0 8px 8px 0;
    cursor: pointer;

    &:hover {
      width: 22px;
      background-color: var(--color-blue-gray--100);
      border-color: var(--color-blue-gray--150);

      .arrow {
        --svg-stroke: var(--color-blue-gray--800);
      }
    }

    :global(.tooltip-container) {
      justify-content: center !important;
    }

    :global(.tooltip-container .tooltip.right) {
      transform: translateX(58%) !important;
    }

    :global(.tooltip-container:hover .tooltip.right) {
      transform: translateX(62%) !important;
    }
  }

  .arrow {
    cursor: pointer;
    background-color: transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    margin: 0;
    margin-left: -2px;
    padding: 0;
    --svg-stroke: var(--color-blue-gray--300);
  }

  .badge {
    position: absolute;
    top: 16px;
    right: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 4px 8px 4px 6px;
    gap: 4px;
    @include font(Semibold);
    font-size: 14px;
    line-height: 20px;
    color: var(--color-blue-gray--200) !important;
    background-color: var(--color-blue-gray--50) !important;
    border-radius: 20px;
    margin-right: 24px;
    user-select: none;
    cursor: pointer;
    z-index: 2;

    :global(svg path) {
      fill: var(--color-blue-gray--200) !important;
    }
  }

  // .product-hunt {
  //   position: fixed;
  //   bottom: 20px;
  //   left: 208px;
  //   display: flex;
  //   width: 328px;
  //   height: 162px;
  //   pointer-events: none;
  //   z-index: 6;

  //   .close {
  //     position: absolute;
  //     right: 7px;
  //     top: 9px;
  //     display: flex;
  //     justify-content: center;
  //     align-items: center;
  //     width: 20px;
  //     height: 20px;
  //     background: #ffffff;
  //     box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15);
  //     border: none;
  //     border-radius: 50px;
  //     cursor: pointer;
  //     opacity: 0;
  //     transform: translateY(100%);
  //     transition: all 0.2s ease-in;
  //     z-index: 2;

  //     :global(svg path) {
  //       stroke: black;
  //     }
  //   }

  //   img {
  //     margin-top: auto;
  //     width: 58px;
  //     height: 58px;
  //     opacity: 0;
  //     transform: translateY(100%);
  //     transition: all 0.4s ease-in;
  //     user-select: none;
  //   }

  //   p {
  //     @include font(Medium);
  //     font-size: 13px;
  //     line-height: 20px;
  //     color: var(--color-blue-gray--50);
  //     background-image: url('/img/product-hunt.png');
  //     background-size: contain;
  //     background-repeat: no-repeat;
  //     padding: 24px 13px 29px 25px;
  //     margin-bottom: 20px;
  //     opacity: 0;
  //     transform: translateY(100%);
  //     transition: all 0.5s ease-in;

  //     a {
  //       color: var(--color-green--500);
  //       text-decoration: underline;
  //     }
  //   }

  //   .visible {
  //     opacity: 1;
  //     transform: translateY(0px);
  //     pointer-events: all;
  //   }
  // }

  .newsletter,
  .daily-report {
    width: 0px;
    opacity: 0;
    pointer-events: none;
    z-index: -1;

    &.visible {
      width: 100%;
      opacity: 1;
      pointer-events: all;
      z-index: 5;
    }
  }

  @media screen and (max-width: 600px) {
    .badge {
      min-width: 48px !important;
      align-self: center;
      color: var(--color-blue-gray--200);
      background-color: var(--color-blue-gray--50);

      :global(svg path) {
        fill: var(--color-blue-gray--200) !important;
      }
    }
  }
  :global(.categories .rs-list .rsli.active) {
    @media screen and (min-width: 601px) {
      background: linear-gradient(90deg, #f7f7f8 0%, rgba(247, 247, 248, 0) 100%);
    }
  }

  :global(.sources .rs-list .rsli.active) {
    @media screen and (min-width: 601px) {
      background: linear-gradient(90deg, #f7f7f8 0%, rgba(247, 247, 248, 0) 100%),
        linear-gradient(90deg, rgba(255, 212, 123, 0.2) 0%, rgba(255, 212, 123, 0) 100%);
    }
  }

  :global(.dark-mode .categories .rs-list .rsli.active) {
    @media screen and (min-width: 601px) {
      background: linear-gradient(90deg, #303d56 0%, rgba(21, 25, 34, 0) 100%);
    }
  }

  :global(.dark-mode .sources .rs-list .rsli.active) {
    @media screen and (min-width: 601px) {
      background: linear-gradient(90deg, #303d56 0%, rgba(21, 25, 34, 0) 100%);
    }
  }

  :global(.rs-list .rsli .tooltip-container) {
    justify-content: initial !important;
  }

  :global(.dark-mode) {
    background-color: var(--color-blue-gray--800);
    .collapse-container {
      background-color: var(--color-blue-gray--700);
      border-color: var(--color-blue-gray--600);

      &:hover {
        background-color: var(--color-blue-gray--600);
        border-color: var(--color-blue-gray--500);

        .arrow {
          --svg-stroke: var(--color-blue-gray--150);
        }
      }
    }

    .badge {
      color: var(--color-blue-gray--300) !important;
      background-color: var(--color-blue-gray--700) !important;

      :global(svg path) {
        fill: var(--color-blue-gray--300) !important;
      }
    }
  }
</style>
